/**
 * Shared measurement label formatting for Room Planner.
 * Use formatMmLabel with hasDecimal to ensure consistent display across
 * wall measurements, empty spaces, reposition (DistanceEditor), and floor/height.
 * When hasDecimal is true, preserves one decimal place (e.g. 16.5, 550.5).
 * When false, uses integer rounding.
 */

/**
 * Formats a distance in scaled units as an mm label, preserving one decimal when needed so values like 16.5 or 550.5 stay accurate.
 * @param {number} scaledValue - The scaled value to format
 * @param {number} scale - The scale to use
 * @return {string} The formatted mm label
 */
export const formatMmLabelFromScaled = (
    scaledValue: number,
    scale: number
): string => {
    if (!scale || !Number.isFinite(scaledValue)) {
        return '';
    }
    const mm = scaledValue / scale;
    if (!Number.isFinite(mm) || mm < 0) {
        return '';
    }
    const rounded1 = Math.round(mm * 10) / 10;
    if (Number.isInteger(rounded1)) {
        return `${rounded1}`;
    }

    return `${rounded1}`;
};

/**
 * Formats a distance in scaled units as an mm label, preserving one decimal when needed so values like 16.5 or 550.5 stay accurate.
 * @param {number} scaledValue - The scaled value to format
 * @param {number} scale - The scale to use
 * @param {boolean} hasDecimal - Whether to preserve one decimal
 * @return {string} The formatted mm label
 */
export const formatMmLabel = (
    scaledValue: number,
    scale: number,
    hasDecimal: boolean
): string =>
    hasDecimal
        ? formatMmLabelFromScaled(scaledValue, scale)
        : `${Math.round(scaledValue / scale)}`;

/**
 * Returns true if any label string contains a decimal point.
 * @param {Array<string>} labels - Array of labels to check
 * @return {boolean} True if any label string contains a decimal point
 */
export const hasDecimalFromLabels = (labels: (string | undefined)[]): boolean =>
    labels.some((label) => (label ?? '').includes('.'));

/**
 * Reads known dimension fields only (literal keys) so object-injection lint is satisfied.
 * @param {Record<string, unknown>} values - Cabinet userData.values
 * @return {boolean} True if any dimension is a non-integer number
 */
function hasNonIntegerNumberInDimensionValues(
    values: Record<string, unknown>
): boolean {
    const candidates: unknown[] = [
        values['cabinet_width'],
        values['cabinet_depth'],
        values['cabinet_height'],
        values['cabinet_panel_width'],
        values['cabinet_panel_length'],
        values['width2'],
        values['toe_kick_height'],
    ];

    for (const val of candidates) {
        if (typeof val === 'number' && !Number.isInteger(val)) {
            return true;
        }
    }
    return false;
}

/**
 * Returns true if any cabinet has decimal dimensions in userData.values.
 * @param {Array} cabinets - Array of cabinet objects with optional userData.values
 * @return {boolean} True if any cabinet has decimal dimensions
 */
export const hasDecimalFromCabinets = (
    cabinets: Array<{userData?: {values?: Record<string, unknown>}}>
): boolean => {
    for (const cabinet of cabinets) {
        const values = cabinet?.userData?.values;
        if (!values) continue;
        if (hasNonIntegerNumberInDimensionValues(values)) {
            return true;
        }
    }

    return false;
};
